// ─────────────────────────────────────────────────────────────────────────────
// LURA Design System — Registro único de temas
// Fonte de verdade para: label canônico, tier, preço, modo estrutural,
// fontes, paleta de export e preview.
//
// Substitui: src/config/themes.ts + src/features/store/config/storeCatalog.ts
// (mantidos temporariamente por compatibilidade; removidos na Fase 2 completa)
// ─────────────────────────────────────────────────────────────────────────────

import { isSeasonOpen } from './themeSeasons'
import { isThemeEventOpen } from './themeEvents'

/**
 * `founder` não é "um PRO mais caro" — é o único tier que NÃO se compra com
 * dinheiro nem com moeda: só chega por `subscriptions.isLifetime`, e por isso
 * fica fora da Loja e fora do onboarding. Quem separa é o gate, não o preço:
 * `themeRequiresFounder` é consultado antes do guard de PRO em
 * `resolveEquippedThemeId`, senão um assinante comum equiparia o tema do
 * fundador só por ter assinatura ativa.
 */
/**
 * `earned` é o segundo tier que não se compra: o direito vem de uma badge, e o
 * gate real mora em `resolveEquippedThemeId` (via `THEME_BADGE_REQUIREMENT` no
 * legado). Como `founder`, sai da Loja e do onboarding — mas por razão
 * diferente: lá é "ninguém pode pagar", aqui é "conta nova não tem badge
 * nenhuma", e três cards permanentemente travados na primeira tela do produto
 * não vendem nada.
 */
export type ThemeTier = 'free' | 'coin-gated' | 'pro' | 'premium' | 'founder' | 'earned'
export type ThemeMode = 'standard' | 'neon-glass'

export interface ThemeDefinition {
  id: string
  /** Nome canônico — único lugar onde esse nome existe. Usado em Settings, Loja e Onboarding. */
  label: string
  description: string
  tier: ThemeTier
  /** Preço em moedas — presente apenas para tier 'coin-gated' */
  price?: { coins: number }
  /** Badge que concede o tema — presente apenas para tier 'earned'. */
  requiresBadge?: string
  /**
   * Id da temporada em `themeSeasons.ts` — presente apenas em tema SAZONAL.
   *
   * ⚠️ Sazonal NÃO é um tier, e a distinção não é estilo. O union `ThemeTier`
   * é consultado por quatro superfícies que decidem por EXCLUSÃO, e um tier
   * novo já quebrou as quatro em silêncio (31/08). Como atributo ortogonal
   * sobre `coin-gated`, o tema sazonal continua em `COIN_GATED_THEME_IDS` e
   * dentro de `FREE_THEME_IDS` (que é o que o guard de PRO consulta), e nada
   * mais precisa mudar.
   *
   * 🔴 A temporada governa **estar à venda**, jamais **ter o direito**. Quem
   * comprou fica com o tema para sempre: o direito é `unlockedThemes`, e
   * `resolveEquippedThemeId` não conhece este campo nem pode passar a
   * conhecer. Ver `docs/systems/seasonal-themes.md` §3.
   */
  season?: string
  /**
   * Id do evento em `themeEvents.ts` — presente apenas em tema de EVENTO
   * (Halloween, 07/10/2026).
   *
   * 🔴 É o oposto do `season`: lá a janela governa a VITRINE e quem comprou
   * fica com o tema; aqui a janela governa o DIREITO. O tema é grátis e
   * emprestado a todo mundo durante o evento; fechou, ele sai de quem o usa e
   * a pessoa volta ao tema de antes (`users.themeBeforeEvent`). Por isso só
   * tema `free` pode declarar evento — tirar da tela um tema pago seria o
   * BUG-80 com item pago (`themeCatalogSync` cobra).
   */
  event?: string
  mode: ThemeMode
  fonts: {
    heading: string
    body: string
    display?: string
  }
  /** Hex para html-to-image. Sem CSS variables, sem oklch. */
  export: {
    background: string
    surface: string
    surfaceAlt: string
    border: string
    textPrimary: string
    textMuted: string
    accent: string
    accentSecondary?: string
  }
  preview: {
    /** Caminho relativo à pasta /public */
    thumbnail: string
    accentHex: string
  }
}

export const THEMES: ThemeDefinition[] = [
  {
    // Redesign 07/2026: 'default' agora é o Linho (decisão do fundador) —
    // o id continua 'default' pra não migrar themePreference salvo.
    id: 'default',
    label: 'Linho',
    description: 'Terracota quente sobre papel — o padrão editorial.',
    tier: 'free',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#f4f1ec',
      surface:         '#ffffff',
      surfaceAlt:      '#efe9dd',
      border:          '#e6dfd2',
      textPrimary:     '#2a2521',
      textMuted:       '#6f675c',
      accent:          '#bc4b2f',
      accentSecondary: '#a86f1d',
    },
    preview: { thumbnail: '/themes/linho.jpg', accentHex: '#bc4b2f' },
  },
  {
    // Ex-default (Cozy Dark) — preservado como tema free selecionável.
    id: 'cozy',
    label: 'Cozy Dark',
    description: 'Quente, aconchegante e literário.',
    tier: 'free',
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter' },
    export: {
      background:      '#101010',
      surface:         '#1a1a1a',
      surfaceAlt:      '#252525',
      border:          '#2a2a2a',
      textPrimary:     '#f1dfc8',
      textMuted:       '#a08060',
      accent:          '#f1dfc8',
      accentSecondary: '#dfb682',
    },
    preview: { thumbnail: '/themes/default.jpg', accentHex: '#f1dfc8' },
  },
  {
    id: 'noir',
    label: 'Noir',
    description: 'Neutro escuro e minimalista.',
    tier: 'free',
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter' },
    export: {
      background:      '#111111',
      surface:         '#1c1c1c',
      surfaceAlt:      '#252525',
      border:          '#2a2a2a',
      textPrimary:     '#e2e2e2',
      textMuted:       '#a3a3a3',
      accent:          '#e2e2e2',
      accentSecondary: '#888888',
    },
    preview: { thumbnail: '/themes/noir.jpg', accentHex: '#e2e2e2' },
  },
  {
    id: 'paper',
    label: 'Papel Antigo',
    description: 'Pergaminho sépia, tipografia clássica e vintage.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter' },
    export: {
      background:      '#f0e6d3',
      surface:         '#e8d9c0',
      surfaceAlt:      '#ddd0b5',
      border:          '#c8b49a',
      textPrimary:     '#3a2206',
      textMuted:       '#755834',
      accent:          '#5c3d11',
      accentSecondary: '#9e7b4a',
    },
    preview: { thumbnail: '/themes/paper.jpg', accentHex: '#5c3d11' },
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    description: 'Fundo roxo profundo, neon ciano e magenta.',
    tier: 'coin-gated',
    price: { coins: 700 },
    mode: 'neon-glass',
    fonts: { heading: 'Inter', body: 'Inter' },
    export: {
      background:      '#08080f',
      surface:         '#0e0e1c',
      surfaceAlt:      '#14142a',
      border:          'rgba(0,245,204,0.18)',
      textPrimary:     '#d0fff6',
      textMuted:       '#4a8a80',
      accent:          '#00f5cc',
      accentSecondary: '#f000b8',
    },
    preview: { thumbnail: '/themes/cyberpunk.jpg', accentHex: '#00f5cc' },
  },
  // ── Temas do redesign (Claude Design, 07/2026) — coin-gated ────────────────
  {
    id: 'cerejeira',
    label: 'Cerejeira',
    description: 'Papel rosado e um vermelho-cereja afetivo.',
    tier: 'free',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#f8f0ee',
      surface:         '#ffffff',
      surfaceAlt:      '#f4eaec',
      border:          '#ecdde0',
      textPrimary:     '#2e2126',
      textMuted:       '#7a626a',
      accent:          '#b23a5f',
      accentSecondary: '#b07d2a',
    },
    preview: { thumbnail: '/themes/cerejeira.jpg', accentHex: '#b23a5f' },
  },
  {
    id: 'eucalipto',
    label: 'Eucalipto',
    description: 'Verde-sálvia calmo, leitura sem pressa.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#eef2ec',
      surface:         '#ffffff',
      surfaceAlt:      '#e7efe6',
      border:          '#dce6dc',
      textPrimary:     '#1f2b23',
      textMuted:       '#5f7065',
      accent:          '#2f6b4f',
      accentSecondary: '#977726',
    },
    preview: { thumbnail: '/themes/eucalipto.jpg', accentHex: '#2f6b4f' },
  },
  {
    id: 'indigo',
    label: 'Índigo',
    description: 'Azul-índigo profundo sobre névoa fria.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#eef0f5',
      surface:         '#ffffff',
      surfaceAlt:      '#e8eaf1',
      border:          '#dde0ea',
      textPrimary:     '#1c2232',
      textMuted:       '#5f667c',
      accent:          '#3a46b0',
      accentSecondary: '#9a7a2e',
    },
    preview: { thumbnail: '/themes/indigo.jpg', accentHex: '#3a46b0' },
  },
  {
    id: 'cafe',
    label: 'Café',
    description: 'Espresso quente pra ler de madrugada.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#17110c',
      surface:         '#211911',
      surfaceAlt:      '#2a2014',
      border:          '#342718',
      textPrimary:     '#f1e7da',
      textMuted:       '#ab9685',
      accent:          '#e3674a',
      accentSecondary: '#e0b352',
    },
    preview: { thumbnail: '/themes/cafe.jpg', accentHex: '#e3674a' },
  },
  {
    id: 'tinta',
    label: 'Tinta',
    description: 'Tinta sépia sobre papel, quase monocromático.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#f3f1ec',
      surface:         '#ffffff',
      surfaceAlt:      '#efeae0',
      border:          '#e4e0d6',
      textPrimary:     '#232019',
      textMuted:       '#6f6a5f',
      accent:          '#3b2d20',
      accentSecondary: '#9a7d3a',
    },
    preview: { thumbnail: '/themes/tinta.jpg', accentHex: '#3b2d20' },
  },
  // ── Leva 08/2026 (referências do fundador) — coin-gated ───────────────────
  {
    id: 'marinho',
    label: 'Marinho',
    description: 'Creme envelhecido e tinta naval — clássico de bolso.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#fdf3c9',
      surface:         '#fffcf0',
      surfaceAlt:      '#f7e9b4',
      border:          '#eddda3',
      textPrimary:     '#12283f',
      textMuted:       '#5a6a7c',
      accent:          '#0f3c65',
      accentSecondary: '#b07d1f',
    },
    preview: { thumbnail: '/themes/marinho.jpg', accentHex: '#0f3c65' },
  },
  {
    id: 'ameixa',
    label: 'Ameixa',
    description: 'Lavanda clara com um roxo de ameixa madura.',
    tier: 'coin-gated',
    price: { coins: 400 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#f4eff5',
      surface:         '#ffffff',
      surfaceAlt:      '#ece4ee',
      border:          '#e2d7e5',
      textPrimary:     '#2a1626',
      textMuted:       '#6f5a6b',
      accent:          '#763a69',
      accentSecondary: '#b26599',
    },
    preview: { thumbnail: '/themes/ameixa.jpg', accentHex: '#763a69' },
  },
  {
    id: 'veludo',
    label: 'Veludo',
    description: 'Oxblood profundo e rosa chiclete — romance sem pudor.',
    tier: 'coin-gated',
    price: { coins: 700 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#1a0505',
      surface:         '#260909',
      surfaceAlt:      '#360d0d',
      border:          '#471414',
      textPrimary:     '#fbe9ef',
      textMuted:       '#c79aa6',
      accent:          '#f698bd',
      accentSecondary: '#e0a95c',
    },
    preview: { thumbnail: '/themes/veludo.jpg', accentHex: '#f698bd' },
  },
  {
    id: 'absinto',
    label: 'Absinto',
    description: 'Púrpura de licor com um verde-limão elétrico.',
    tier: 'coin-gated',
    price: { coins: 700 },
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#1a0410',
      surface:         '#280719',
      surfaceAlt:      '#380a22',
      border:          '#4b0f2d',
      textPrimary:     '#f3eee0',
      textMuted:       '#bb9cab',
      accent:          '#cad183',
      accentSecondary: '#dfae5c',
    },
    preview: { thumbnail: '/themes/absinto.jpg', accentHex: '#cad183' },
  },
  // ── Temporada Eclipse (2026) — os dois primeiros temas SAZONAIS ───────────
  // Ficam na Loja de 01/10 a 30/11 e saem sozinhos. Quem comprar fica com eles
  // para sempre. Blueprint: docs/systems/seasonal-themes.md
  //
  // Eles não são só "mais um claro e mais um escuro": o par se separa dos 24
  // no eixo ESTRUTURAL (Lua é difusa — sombra larga, borda quase ausente,
  // superfície velada; Sol é nítido — sombra curta e dura, borda forte), e
  // cada um pinta uma camada ambiente própria (`--theme-ambient`). Só a cor
  // não os distinguiria de midnight e de Linho.
  {
    id: 'lua',
    label: 'Lua',
    description: 'Noite fria de prata e azul pálido, sob um brilho lunar.',
    tier: 'coin-gated',
    price: { coins: 1500 },
    season: 'eclipse',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#100a2e',
      surface:         '#1b1443',
      surfaceAlt:      '#251c58',
      border:          '#332876',
      textPrimary:     '#ece9fc',
      textMuted:       '#a9a1d4',
      accent:          '#a8b6f0',
      accentSecondary: '#d9d4f2',
    },
    preview: { thumbnail: '/themes/lua.jpg', accentHex: '#a8b6f0' },
  },
  {
    id: 'sol',
    label: 'Sol',
    description: 'Dia quente de creme e âmbar, com o sol batendo de cima.',
    tier: 'coin-gated',
    price: { coins: 1500 },
    season: 'eclipse',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#fff0dd',
      surface:         '#fffaf3',
      surfaceAlt:      '#ffe3bf',
      border:          '#f2d5ab',
      textPrimary:     '#2e1e07',
      textMuted:       '#6d5329',
      accent:          '#ba5009',
      accentSecondary: '#9a4a12',
    },
    preview: { thumbnail: '/themes/sol.jpg', accentHex: '#ba5009' },
  },
  // ── Evento: Halloween (07/10/2026) ──────────────────────────────────────────
  // Mock aprovado `redesign-livra/tema-halloween/` (decisão ④ A). Grátis para
  // todos de 15/10 a 02/11 (`themeEvents.ts`); fora disso, não existe.
  //
  // O papel é VIOLETA-noite, não marrom, e é ele que separa o tema de Café e
  // Brasa (os dois já são "escuro + laranja"): medido contra os 31 temas,
  // nenhuma colisão em `palettesLookSame`; vizinhos mais perto Diamante e Lua
  // (papel 14, tinta 54/56). O verde de caldeirão foi testado e reprovado —
  // o papel esverdeado cai a 11 do Dark Academia com tinta parecida.
  {
    id: 'halloween',
    label: 'Halloween',
    description: 'Noite violeta, abóbora e uma visita de oito patas.',
    tier: 'free',
    event: 'halloween',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#160b21',
      surface:         '#241332',
      surfaceAlt:      '#301b42',
      border:          '#452a5a',
      textPrimary:     '#fbe4c6',
      textMuted:       '#bfa9c4',
      // A abóbora é o acento (botões, "ver lista"); o lilás é o `--gold`, que
      // o app usa como TEXTO — 8,66:1 sobre o papel.
      accent:          '#ff7f1f',
      accentSecondary: '#c79bff',
    },
    preview: { thumbnail: '/themes/halloween.jpg', accentHex: '#ff7f1f' },
  },
  {
    id: 'midnight',
    label: 'Midnight',
    description: 'Sombrio, intenso e dramático.',
    tier: 'premium',
    mode: 'standard',
    fonts: { heading: 'Cinzel', body: 'Roboto' },
    export: {
      background:      '#0a090e',
      surface:         '#14121f',
      surfaceAlt:      '#1e1a2e',
      border:          '#231a2e',
      textPrimary:     '#f2eaf0',
      textMuted:       '#b078a0',
      accent:          '#d04e6e',
      accentSecondary: '#7d1f35',
    },
    preview: { thumbnail: '/themes/midnight.jpg', accentHex: '#bf3f5a' },
  },
  {
    id: 'matcha',
    label: 'Matcha Cottagecore',
    description: 'Natural, sereno e encantador.',
    tier: 'premium',
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter' },
    export: {
      background:      '#F2EFE9',
      surface:         '#E5E2D9',
      surfaceAlt:      '#D8D5CB',
      border:          '#CDD0C5',
      textPrimary:     '#35422f',
      textMuted:       '#4f5e49',
      accent:          '#505c49',
      accentSecondary: '#9EB096',
    },
    preview: { thumbnail: '/themes/matcha.jpg', accentHex: '#6A7B62' },
  },
  {
    id: 'obsidian',
    label: 'Obsidian Pulse',
    description: 'Preto absoluto, violeta e ciano — profundidade neon-glass.',
    tier: 'pro',
    mode: 'neon-glass',
    fonts: { heading: 'Playfair Display', body: 'Inter' },
    export: {
      background:      '#050505',
      surface:         '#0a0a0a',
      surfaceAlt:      '#121212',
      border:          'rgba(255,255,255,0.1)',
      textPrimary:     '#fafafa',
      textMuted:       '#7c7c86',
      accent:          '#a78bfa',
      accentSecondary: '#22d3ee',
    },
    preview: { thumbnail: '/themes/obsidian.jpg', accentHex: '#a78bfa' },
  },
  // ── Leva 08/2026 (referências do fundador) — assinatura ───────────────────
  {
    id: 'borgonha',
    label: 'Borgonha',
    description: 'Preto absoluto, vinho e off-white. Nada sobra.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#0a0908',
      surface:         '#15100f',
      // O #49111c da referência é PAPEL, não accent: como accent ele dá 1,5:1
      // sobre o preto. O accent é o vinho erguido (#d4566b, 5,06:1).
      surfaceAlt:      '#49111c',
      border:          '#3a1119',
      textPrimary:     '#f2f4f3',
      textMuted:       '#a79b9d',
      accent:          '#d4566b',
      accentSecondary: '#c2a15a',
    },
    preview: { thumbnail: '/themes/borgonha.jpg', accentHex: '#d4566b' },
  },
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Violeta, índigo e um teal frio — a luz do fim da tarde.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      // Único tema em que a superfície elevada muda de MATIZ em vez de só de
      // luminosidade (índigo sobre violeta) — é o que produz o efeito aurora.
      background:      '#2b193d',
      surface:         '#2c365e',
      surfaceAlt:      '#484d6d',
      border:          '#3d4468',
      textPrimary:     '#eae6f2',
      textMuted:       '#a9a3c0',
      // Teal da referência clareado: o #4b8f8c original dá 4,1:1 e reprova.
      accent:          '#6cbdb8',
      accentSecondary: '#c5979d',
    },
    preview: { thumbnail: '/themes/aurora.jpg', accentHex: '#6cbdb8' },
  },
  // ── Founder — não se compra ───────────────────────────────────────────────
  {
    id: 'latao',
    label: 'Latão',
    description: 'Preto de cofre e latão polido — a placa gravada virou tema.',
    tier: 'founder',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      // O accent (#c9a355) é o MESMO latão do meio do gradiente da
      // `FounderPlaque` — o tema não "combina com" a placa, ele é feito da
      // mesma liga. `accentSecondary` é o realce especular dela (#e8cf9a).
      background:      '#0b0a08',
      surface:         '#16130e',
      surfaceAlt:      '#241d12',
      border:          '#3a2f1d',
      textPrimary:     '#f4efe4',
      textMuted:       '#aa9d86',
      accent:          '#c9a355',
      accentSecondary: '#e8cf9a',
    },
    preview: { thumbnail: '/themes/latao.jpg', accentHex: '#c9a355' },
  },
  {
    id: 'alcova',
    label: 'Alcova',
    description: 'Rosa empoeirado sob luz de abajur.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#1e1519',
      surface:         '#291d23',
      surfaceAlt:      '#372630',
      border:          '#44303a',
      textPrimary:     '#f6e7ec',
      textMuted:       '#c2a3ae',
      accent:          '#e59aa4',
      accentSecondary: '#dcb079',
    },
    preview: { thumbnail: '/themes/alcova.jpg', accentHex: '#e59aa4' },
  },
  // ── Leva PRO 09/2026 — os dois primeiros com CAMADA AMBIENTE no perfil ────
  //
  // Além da paleta, estes dois mudam a FONTE e ganham fundo vivo no perfil
  // (`config/themeAmbient.ts`). Eram os três eixos que o PRO tinha disponíveis
  // e não usava: os quatro temas de assinatura anteriores compartilham a mesma
  // Fraunces e o mesmo fundo chapado dos gratuitos.
  {
    id: 'sakura',
    label: 'Sakura',
    description: 'Rosa de primavera com pétalas caindo no seu perfil.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter', display: 'Playfair Display' },
    export: {
      background:      '#ffe4ee',
      surface:         '#fff7fa',
      surfaceAlt:      '#ffd3e2',
      border:          '#f4b8ce',
      textPrimary:     '#3f1024',
      textMuted:       '#8a4a63',
      accent:          '#c62d63',
      // Dourado a 80% do tom cheio: `--gold` é usado como TEXTO em várias
      // superfícies, então a régua dele é 4,5 e não 3.
      accentSecondary: '#8d5e23',
    },
    preview: { thumbnail: '/themes/sakura.jpg', accentHex: '#c62d63' },
  },
  {
    id: 'vitral',
    label: 'Vitral',
    description: 'Luz de catedral em Cinzel, com poeira colorida no perfil.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Cinzel', body: 'Inter', display: 'Cinzel' },
    export: {
      background:      '#e8e4f5',
      surface:         '#f7f5fd',
      surfaceAlt:      '#dcd6ef',
      border:          '#c3b9e0',
      textPrimary:     '#1d1636',
      textMuted:       '#5d5480',
      accent:          '#5b3fa8',
      accentSecondary: '#286d90',
    },
    preview: { thumbnail: '/themes/vitral.jpg', accentHex: '#5b3fa8' },
  },
  // ── Leva PRO F3 (25/09/2026) — estéticas BookTok ────────────────────────
  //
  // Escolha do fundador sobre o mock `redesign-livra/loja-cores-temas/` (seção
  // C): Romantasy e Dark Academia seguem; Cottagecore saiu (a Matcha ganhou as
  // folhas de chá); Planta Baixa ficou parada. Paletas MEDIDAS: portão AA com o
  // véu composto (`newThemesContrast`), fora de `palettesLookSame` com margem
  // (papel a 18 e 16 do vizinho mais próximo; o teto é 12) e ΔE ≥ 36 de todo
  // tema do catálogo — nenhum repete o Eucalipto ≈ Matcha.
  {
    id: 'romantasy',
    label: 'Romantasy',
    description: 'Noite de ameixa e rosa antigo, com pétalas e ouro caindo no seu perfil.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Playfair Display', body: 'Inter', display: 'Playfair Display' },
    export: {
      background:      '#220d2b',
      surface:         '#2f173a',
      surfaceAlt:      '#3e204b',
      border:          '#4a2a58',
      textPrimary:     '#f7e8f1',
      textMuted:       '#c9aecb',
      accent:          '#f08fb8',
      accentSecondary: '#e2b86b',
    },
    preview: { thumbnail: '/themes/romantasy.jpg', accentHex: '#f08fb8' },
  },
  {
    id: 'academia',
    label: 'Dark Academia',
    description: 'Verde de biblioteca e papel envelhecido, com luz de janela e poeira no seu perfil.',
    tier: 'pro',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#0e2016',
      surface:         '#172a1f',
      surfaceAlt:      '#20352a',
      border:          '#2f4436',
      textPrimary:     '#ece6d4',
      textMuted:       '#b1ad96',
      accent:          '#9ec48a',
      accentSecondary: '#d0a55c',
    },
    preview: { thumbnail: '/themes/academia.jpg', accentHex: '#9ec48a' },
  },
  // ── Tier `earned` (31/08) ─────────────────────────────────────────────────
  {
    id: 'diamante',
    label: 'Diamante',
    description: 'Gelo sobre grafite — o azul frio do topo da liga.',
    tier: 'earned',
    requiresBadge: 'imperador_diamante',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#0c141d',
      surface:         '#131e2a',
      surfaceAlt:      '#1c2a38',
      border:          '#28394a',
      textPrimary:     '#e4eef6',
      textMuted:       '#93a7b8',
      accent:          '#5cc8e8',
      accentSecondary: '#a8d8ea',
    },
    preview: { thumbnail: '/themes/diamante.jpg', accentHex: '#5cc8e8' },
  },
  {
    id: 'brasa',
    label: 'Brasa',
    description: 'Carvão e laranja vivo — a chama que não apagou.',
    tier: 'earned',
    requiresBadge: 'ofensiva_100',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      background:      '#1f1512',
      surface:         '#2b1d17',
      surfaceAlt:      '#3a2820',
      border:          '#4a3327',
      textPrimary:     '#ffe2bd',
      textMuted:       '#c0a086',
      accent:          '#f2622a',
      accentSecondary: '#f2a63a',
    },
    preview: { thumbnail: '/themes/brasa.jpg', accentHex: '#f2622a' },
  },
  {
    id: 'grifo',
    label: 'Grifo',
    description: 'Papel de caderno e marca-texto amarelo.',
    tier: 'earned',
    requiresBadge: 'critico_publico',
    mode: 'standard',
    fonts: { heading: 'Fraunces', body: 'Inter', display: 'Fraunces' },
    export: {
      // O accent é a ARESTA ESCURA do marcador, não o amarelo cru: medido,
      // #e5b800 sobre o papel dá 1,84:1 — abaixo até do piso de 3:1 de
      // elemento gráfico, e seria o menor do catálogo inteiro. O amarelo vivo
      // vive onde ele é fundo (surfaceAlt e --gold), que é o que um
      // marca-texto de fato é.
      background:      '#fffdf2',
      surface:         '#ffffff',
      surfaceAlt:      '#fdf3c0',
      border:          '#e9e0bc',
      textPrimary:     '#26241c',
      textMuted:       '#6b6553',
      accent:          '#8a6d0e',
      accentSecondary: '#b8890c',
    },
    preview: { thumbnail: '/themes/grifo.jpg', accentHex: '#8a6d0e' },
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Retorna o tema pelo id; fallback para o primeiro (default). */
export const getTheme = (id: string): ThemeDefinition =>
  THEMES.find(t => t.id === id) ?? THEMES[0]

export const getFreeThemes = (): ThemeDefinition[] =>
  THEMES.filter(t => t.tier === 'free')

export const getCoinGatedThemes = (): ThemeDefinition[] =>
  THEMES.filter(t => t.tier === 'coin-gated')

export const getProThemes = (): ThemeDefinition[] =>
  THEMES.filter(t => t.tier === 'pro' || t.tier === 'premium')

/** IDs de temas gratuitos — usado no ThemeContext e onboarding. */
export const FREE_THEME_IDS: string[] = THEMES
  .filter(t => t.tier === 'free')
  .map(t => t.id)

/** IDs de temas que requerem compra com moedas. */
export const COIN_GATED_THEME_IDS: string[] = THEMES
  .filter(t => t.tier === 'coin-gated')
  .map(t => t.id)

/** IDs de temas que requerem assinatura (pro ou premium). */
export const SUBSCRIPTION_THEME_IDS: string[] = THEMES
  .filter(t => t.tier === 'pro' || t.tier === 'premium')
  .map(t => t.id)

/** Tema requer assinatura ativa? */
export const themeRequiresSubscription = (id: string): boolean =>
  SUBSCRIPTION_THEME_IDS.includes(id)

/** IDs de temas conquistados por badge — não entram em loja nem em onboarding. */
export const EARNED_THEME_IDS: string[] = THEMES
  .filter(t => t.tier === 'earned')
  .map(t => t.id)

/** Tema exige badge? Espelhado em `config/themes.ts` (legado), que é de onde o gate lê. */
export const themeRequiresBadge = (id: string): boolean =>
  EARNED_THEME_IDS.includes(id)

/** IDs de temas exclusivos de Founder — não entram em loja nem em onboarding. */
export const FOUNDER_THEME_IDS: string[] = THEMES
  .filter(t => t.tier === 'founder')
  .map(t => t.id)

/**
 * Tema exige ser Founder? Espelhado em `config/themes.ts` (legado) porque
 * `resolveEquippedThemeId` lê de lá — a paridade das duas listas é travada por
 * teste enquanto a Fase 2 da migração não fecha.
 */
export const themeRequiresFounder = (id: string): boolean =>
  FOUNDER_THEME_IDS.includes(id)

/** Modo estrutural do tema (para BottomNav, headers, sheets). */
export const getThemeMode = (id: string): ThemeMode =>
  getTheme(id).mode

/**
 * Temas disponíveis para exibição na Loja (não-subscription).
 *
 * Founder sai daqui porque a Loja é onde se COMPRA: um card com preço que
 * ninguém pode pagar é vitrine de coisa inalcançável, não upsell. Ele também
 * não entra no `getProThemes` (o teaser de assinatura) pela mesma razão —
 * assinar não destrava. O mesmo vale para `earned`.
 *
 * ⚠️ O filtro é por INCLUSÃO (`free` ou `coin-gated`), e isso não é estilo:
 * na forma anterior — exclusão de pro/premium/founder — todo tier novo caía
 * na Loja sozinho, com preço, sem ninguém notar. Foi o que aconteceria com
 * `earned` em 31/08.
 */
export const getStoreThemes = (at: Date = new Date()): ThemeDefinition[] =>
  THEMES.filter(
    t => (t.tier === 'free' || t.tier === 'coin-gated') && isThemeSellable(t, at),
  )

/**
 * O tema pode ser COMPRADO agora?
 *
 * Tema comum: sempre. Tema sazonal: só dentro da janela da temporada dele.
 *
 * 🔴 Isto NÃO é "o tema pode ser equipado". Quem comprou um sazonal continua
 * com ele depois que a temporada fecha, e é `resolveEquippedThemeId` que
 * responde por isso, lendo `unlockedThemes`. Chamar esta função de dentro do
 * resolvedor faria todo comprador perder o tema no dia do fechamento, em
 * silêncio, no app e no perfil público ao mesmo tempo — o BUG-80 outra vez,
 * agora com item pago. Há teste travando essa separação.
 *
 * `at` é injetável para o teste poder viajar no tempo sem fake timers.
 */
export const isThemeSellable = (
  theme: ThemeDefinition | string,
  at: Date = new Date(),
): boolean => {
  const t = typeof theme === 'string' ? THEMES.find(x => x.id === theme) : theme
  if (!t) return false
  // Tema de evento é grátis: "vendável" aqui é só "existe agora". Ele não tem
  // card de compra na Loja (que lista `coinPrice > 0`); quem o oferece é o
  // `EventThemeCard`. Fora do evento ele não aparece em vitrine nenhuma.
  if (t.event) return isThemeEventOpen(t.event, at)
  if (!t.season) return true
  return isSeasonOpen(t.season, at)
}

/** IDs de temas sazonais, em qualquer estado de janela. Derivado, nunca à mão. */
export const SEASONAL_THEME_IDS: string[] = THEMES
  .filter(t => typeof t.season === 'string' && t.season.length > 0)
  .map(t => t.id)

/** A temporada de um tema, ou `null` se ele não é sazonal. */
export const seasonIdForTheme = (id: string): string | null =>
  THEMES.find(t => t.id === id)?.season ?? null

/** IDs de temas de evento, em qualquer estado de janela. Derivado, nunca à mão. */
export const EVENT_THEME_IDS: string[] = THEMES
  .filter(t => typeof t.event === 'string' && t.event.length > 0)
  .map(t => t.id)

/** O evento de um tema, ou `null` se ele não é de evento. */
export const eventIdForTheme = (id: string): string | null =>
  THEMES.find(t => t.id === id)?.event ?? null

/** Temas de evento com o evento no ar agora — os que a Loja oferece de graça. */
export const getLiveEventThemes = (at: Date = new Date()): ThemeDefinition[] =>
  THEMES.filter(t => t.tier === 'free' && t.event && isThemeEventOpen(t.event, at))

// ─────────────────────────────────────────────────────────────────────────────
// @deprecated — Tipos e funções de compatibilidade legados
// Movidos de src/config/themes.ts. Não usar em código novo.
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use ThemeMode */
export type ThemeStyleType = ThemeMode

/** @deprecated Use ThemeDefinition */
export interface ThemeOption {
  id: string
  label: string
  description: string
  sublabel: string
  isPro: boolean
  isPremium?: boolean
  styleType?: ThemeStyleType
  palette: {
    bg: string
    surface: string
    primary: string
    secondary: string
  }
}

/** @deprecated Use ThemeDefinition['export'] */
export interface ExportPalette {
  bg: string
  surface: string
  surfaceAlt: string
  border: string
  primary: string
  secondary: string
  muted: string
  faint: string
}

function toThemeOption(t: ThemeDefinition): ThemeOption {
  return {
    id:          t.id,
    label:       t.label,
    description: t.description,
    sublabel:    t.tier === 'free' ? 'Gratuito'
                 : t.tier === 'coin-gated' ? `${t.price!.coins} moedas`
                 : t.tier === 'founder' ? 'Livra Founder'
                 : 'Livra PRO',
    // `isPro` do tipo legado significa "exige assinatura". Founder NÃO entra:
    // marcar true faria o tema aparecer no teaser de PRO do onboarding,
    // vendendo assinatura que não o destrava.
    isPro:       t.tier === 'pro' || t.tier === 'premium',
    isPremium:   t.tier === 'premium',
    styleType:   t.mode,
    palette: {
      bg:        t.export.background,
      surface:   t.export.surface,
      primary:   t.export.accent,
      secondary: t.export.accentSecondary ?? t.export.accent,
    },
  }
}

function toExportPaletteCompat(t: ThemeDefinition): ExportPalette {
  return {
    bg:         t.export.background,
    surface:    t.export.surface,
    surfaceAlt: t.export.surfaceAlt,
    border:     t.export.border,
    primary:    t.export.textPrimary,
    secondary:  t.export.accent,
    muted:      t.export.textMuted,
    faint:      t.export.surfaceAlt,
  }
}

/** @deprecated Use THEMES (ThemeDefinition[]) from themeRegistry */
export const THEME_OPTIONS: ThemeOption[] = THEMES.map(toThemeOption)

/** @deprecated Use THEME_OPTIONS */
export const THEMES_COMPAT: ThemeOption[] = THEME_OPTIONS

/** @deprecated Use getTheme(id).export */
export const EXPORT_PALETTES: Record<string, ExportPalette> = Object.fromEntries(
  THEMES.map(t => [t.id, toExportPaletteCompat(t)])
)

/** @deprecated Use getTheme(id) */
export function getThemeById(themeId: string): ThemeOption | undefined {
  const t = getTheme(themeId)
  return t ? toThemeOption(t) : undefined
}

/** @deprecated Use getThemeMode(id) */
export function getThemeStyleType(themeId: string): ThemeStyleType {
  return getThemeMode(themeId)
}

/** @deprecated Use getTheme(id).export */
export function getExportPalette(themeId: string | undefined): ExportPalette {
  return toExportPaletteCompat(getTheme(themeId ?? 'default'))
}
